// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

// --- RxJs
import { Subscription, BehaviorSubject, Observable, combineLatest } from 'rxjs';
import {
  map,
  startWith,
  debounceTime,
  distinctUntilChanged,
  share,
} from 'rxjs/operators';

// --- 3rd Party

// --- HS Code
import {
  SearchStates,
  SelectListItem,
  SurveySummaryModel,
} from '../../../models';
import moment from 'moment';
import { chain } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hs-compare-report-selection',
  templateUrl: './compare-report-selection.component.html',
  styleUrls: ['./compare-report-selection.component.scss'],
})
export class CompareReportSelectionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() campaigns: SurveySummaryModel[] | null = null;
  @Input() campaignsLoading: boolean | null = null;
  @Input() selectedCampaign: SurveySummaryModel | null = null;
  @Input() selectedSurvey: SurveySummaryModel | null = null;

  @Output() selectCampaign = new EventEmitter<SurveySummaryModel>();

  selectionForm: FormGroup;
  searchState$ = new BehaviorSubject<SearchStates>(SearchStates.initial);
  searchStates = SearchStates;

  private _campaignList$: BehaviorSubject<SurveySummaryModel[]>;
  get campaignList$(): Observable<SurveySummaryModel[]> {
    return this._campaignList$;
  }

  private _yearsList$: BehaviorSubject<SelectListItem[]>;
  get yearsList$(): Observable<SelectListItem[]> {
    return this._yearsList$;
  }
  filteredCampaigns$: Observable<SurveySummaryModel[]> | undefined;

  private _filteredCampaignSubscription: Subscription = Subscription.EMPTY;
  private _searchSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService
  ) {
    this._campaignList$ = new BehaviorSubject(new Array<SurveySummaryModel>());
    this._yearsList$ = new BehaviorSubject(new Array<SelectListItem>());

    this.selectionForm = this.fb.group({
      search: ['', Validators.nullValidator],
      year: ['', Validators.nullValidator],
    });
  }

  ngOnInit() {
    let searchControl = this.selectionForm.get('search');
    let yearControl = this.selectionForm.get('year');

    if (searchControl) {
      this._searchSubscription = searchControl.valueChanges
        .subscribe((searchValue: string) => {
          if (!searchValue) {
            this.searchState$.next(SearchStates.initial);
            return;
          }
          this.searchState$.next(SearchStates.filled);
        });
    }

    if (searchControl && yearControl) {
      this.filteredCampaigns$ = combineLatest([
        this.campaignList$,
        searchControl.valueChanges.pipe(startWith('')),
        yearControl.valueChanges.pipe(startWith('')),
      ]).pipe(
        debounceTime(500),
        distinctUntilChanged(),
        map(([campaigns, value, year]) => {
          return this._filter(campaigns, value, year);
        }),
        share()
      );
    }

    this._filteredCampaignSubscription = this.campaignList$.subscribe(
      (campaigns) => {
        const result = this._getYearsFromCampaigns(campaigns);
        this._yearsList$.next(result);
      }
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['campaigns'] && changes['campaigns'].currentValue) {
      this._campaignList$.next(changes['campaigns'].currentValue);
    }
  }

  ngOnDestroy() {
    if (this._filteredCampaignSubscription) {
      this._filteredCampaignSubscription.unsubscribe();
    }
    if (this._searchSubscription) {
      this._searchSubscription.unsubscribe();
    }
  }

  addonButtonClick() {
    let searchControl = this.selectionForm.get('search');
    
    if (searchControl) {
      switch (this.searchState$.value) {
        case SearchStates.filled:
          searchControl.setValue('');
          break;
      }
    }
  }

  onSelectCampaign(campaign: SurveySummaryModel) {
    this.selectCampaign.emit(campaign);
  }

  private _filter(
    list: SurveySummaryModel[] = [],
    value: string = '',
    year: string = ''
  ): SurveySummaryModel[] {
    const filterValue = this._normalizeValue(value);
    return (
      list
        .filter((d) => this._normalizeValue(d.name).includes(filterValue))
        // filter by year
        .filter((l) => !year || moment(l.dateCreated).format('yyyy') === year)
    );
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  private _getYearsFromCampaigns(
    campaigns: SurveySummaryModel[]
  ): SelectListItem[] {
    let result = campaigns.map((c: SurveySummaryModel): SelectListItem => {
      return {
        text: moment(c.dateCreated).format('yyyy'),
        value: moment(c.dateCreated).format('yyyy'),
        group: '',
        isDisabled: false,
        isSelected: false,
      };
    });
    result = [
      {
        text: this.translate.instant('allCampaigns'),
        value: '',
        group: '',
        isDisabled: false,
        isSelected: true,
      },
      ...result,
    ];
    return chain(result)
      .sortBy('value')
      .unionBy((r) => r.value)
      .value();
  }
}
